import { useState } from 'react';
import { withTranslation } from "next-i18next";
import classNames from 'classnames';
import useCustomizableCloset from '@hooks/use-customizable-closet';
import useFeatures from '@hooks/use-features';
import { useReloadCustomizableCloset, useValidateCurrentCloset } from '@hooks/use-customizable-closet';
import { useCloseDrawer } from "@hooks/use-drawer";
import Button from '@components/core/Button';
import OffcanvasHeader from '@components/core/Offcanvas/OffcanvasHeader';
import ValidationSubheader from './Elements/ValidationSubheader';
import ClosetItemsValidation from './Elements/ClosetItemsValidation';
import ShippingAddressValidation from './Elements/ShippingAddressValidation';
import ReturnModeValidation from './Elements/ReturnModeValidation';
import ValidationSummary from './Elements/ValidationSummary';

function DrawerClosetRTCValidationStep({ setStep, open, t }) {
  const { max_add_on_items: maxAddOnItems } = useFeatures();
  const { ready_to_pack_status: validationStatus, plan } = useCustomizableCloset();
  const validateCurrentCloset = useValidateCurrentCloset();
  const reloadCurrentCloset = useReloadCustomizableCloset();
  const closeDrawer = useCloseDrawer();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function planContent() {
    if (plan.swap_between_families) return t("common:drawer.modal_rtc.plans.swapable_families", { count: plan.clothes + plan.accessories });

    return t("common:drawer.modal_rtc.plans.accessory", { clothesCount: plan.clothes, count: plan.accessories });
  }

  const errorText = () => {
    if (validationStatus.status && !error) return null;

    const errorReason = validationStatus.reason || error;

    switch (errorReason) {
      case "no_address":
        return t("common:drawer.modal_rtc.errors.no_address");
      case "too_many_selected_items":
        return t("common:drawer.modal_rtc.errors.too_many_selected_items", { nbOfArticles: plan.accessories + plan.clothes + maxAddOnItems });
      case "too_few_selected_items":
        return t("common:drawer.modal_rtc.errors.too_few_selected_items", { planContent: planContent() });
      default:
        return t("common:drawer.modal_rtc.errors.validate_closet_error");
    }
  };

  async function validateCloset() {
    if (isLoading) return;

    try {
      setIsLoading(true);
      await validateCurrentCloset();
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data?.code);
      reloadCurrentCloset();
    }
  }

  return (
    <div className={classNames("drawer-additional-panel", open && "open")}>
      <OffcanvasHeader
        closeButton
        onHide={closeDrawer}
        title={t('common:drawer.header.check_and_validate')}
      />

      <div className="drawer-body">
        <ValidationSubheader />

        <div className="d-flex flex-column p-2 gap-2">
          <ClosetItemsValidation setStep={setStep} />

          <ShippingAddressValidation />
          <ReturnModeValidation />
          <ValidationSummary />
        </div>
      </div>

      <div className="p-3 border-top border-light">
        {error && (
          <p className="text-danger font-size-sm mb-3 text-center">
            {errorText()}
          </p>
        )}

        <Button
          loading={isLoading}
          onClick={validateCloset}
          fullWidth
        >
          {t("common:drawer.modal_rtc.validate.validate_btn")}
        </Button>
      </div>
    </div>
  );
}

export default withTranslation("common")(DrawerClosetRTCValidationStep);
